/** QuickHouse — Géométrie des escaliers : contours 2D, marches, boîtes 3D (pur, sans three) */

const GOING = 27; // giron ≈ 27 cm par marche

// ── Chemin (droit / à angles / échelle) ───────────────────────
function pathLength(path) {
  let len = 0;
  for (let i = 1; i < path.length; i++) len += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
  return len;
}

/** Point + direction à l'abscisse u le long du chemin */
function pathAt(path, u) {
  let acc = 0;
  for (let i = 1; i < path.length; i++) {
    const dx = path[i].x - path[i - 1].x;
    const dy = path[i].y - path[i - 1].y;
    const seg = Math.hypot(dx, dy);
    if (u <= acc + seg || i === path.length - 1) {
      const t = seg > 0 ? Math.max(0, Math.min(1, (u - acc) / seg)) : 0;
      return {
        p: { x: path[i - 1].x + dx * t, y: path[i - 1].y + dy * t },
        dir: seg > 0 ? { x: dx / seg, y: dy / seg } : { x: 1, y: 0 },
      };
    }
    acc += seg;
  }
  return { p: { ...path[0] }, dir: { x: 1, y: 0 } };
}

/**
 * Représentation 2D d'un escalier :
 * { edges: [{a, b, t}], steps: [{a, b, t}], arrow: {a, b} }
 * t ∈ [0,1] = progression le long de la montée (pour pointiller la moitié haute).
 */
export function stairPlan(stair) {
  if (stair.kind === 'spiral') return spiralPlan(stair);
  const { path, width } = stair;
  const half = width / 2;
  const total = pathLength(path);
  if (total <= 0) return { edges: [], steps: [], arrow: null };

  const edges = [];
  // Bords : deux polylignes décalées, segment par segment (coins non raccordés : acceptable)
  let acc = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (seg <= 0) continue;
    const nx = -(b.y - a.y) / seg;
    const ny = (b.x - a.x) / seg;
    const tMid = (acc + seg / 2) / total;
    edges.push(
      { a: { x: a.x + nx * half, y: a.y + ny * half }, b: { x: b.x + nx * half, y: b.y + ny * half }, t: tMid },
      { a: { x: a.x - nx * half, y: a.y - ny * half }, b: { x: b.x - nx * half, y: b.y - ny * half }, t: tMid },
    );
    acc += seg;
  }
  // Marches : traits perpendiculaires régulièrement espacés
  const count = Math.max(2, Math.round(total / (stair.kind === 'ladder' ? GOING * 1.5 : GOING)));
  const steps = [];
  for (let i = 0; i <= count; i++) {
    const u = (total * i) / count;
    const { p, dir } = pathAt(path, u);
    const nx = -dir.y;
    const ny = dir.x;
    steps.push({
      a: { x: p.x + nx * half, y: p.y + ny * half },
      b: { x: p.x - nx * half, y: p.y - ny * half },
      t: u / total,
    });
  }
  // Flèche de montée : dernier quart du chemin
  const from = pathAt(path, total * 0.7);
  const to = pathAt(path, total * 0.98);
  return { edges, steps, arrow: { a: from.p, b: to.p } };
}

function spiralPlan(stair) {
  const { center, radius, startAngle = 0, sweep = 360 } = stair;
  const a0 = (startAngle * Math.PI) / 180;
  const sw = (sweep * Math.PI) / 180;
  const steps = [];
  const count = Math.max(6, Math.round(Math.abs(sweep) / 22)); // ~22° par marche
  for (let i = 0; i <= count; i++) {
    const a = a0 + (sw * i) / count;
    steps.push({
      a: { x: center.x, y: center.y },
      b: { x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) },
      t: i / count,
    });
  }
  // Cercle extérieur en petits segments
  const edges = [];
  const segs = 48;
  for (let i = 0; i < segs; i++) {
    const a1 = a0 + (sw * i) / segs;
    const a2 = a0 + (sw * (i + 1)) / segs;
    edges.push({
      a: { x: center.x + radius * Math.cos(a1), y: center.y + radius * Math.sin(a1) },
      b: { x: center.x + radius * Math.cos(a2), y: center.y + radius * Math.sin(a2) },
      t: i / segs,
    });
  }
  const aEnd = a0 + sw;
  const rMid = radius * 0.6;
  return {
    edges,
    steps,
    arrow: {
      a: { x: center.x + rMid * Math.cos(aEnd - sw * 0.25), y: center.y + rMid * Math.sin(aEnd - sw * 0.25) },
      b: { x: center.x + rMid * Math.cos(aEnd), y: center.y + rMid * Math.sin(aEnd) },
    },
  };
}

// ── 3D : marches en boîtes ────────────────────────────────────
/**
 * Boîtes 3D des marches (coordonnées monde, cm) :
 * [{ x, y, angle, going, width, z0, h }] — x/y = centre de la marche,
 * angle = orientation, z0 = altitude du dessous, h = épaisseur.
 */
export function stairToBoxes(stair, rise) {
  const boxes = [];
  const stepH = 4; // épaisseur du plateau de marche
  if (stair.kind === 'spiral') {
    const { center, radius, startAngle = 0, sweep = 360 } = stair;
    const a0 = (startAngle * Math.PI) / 180;
    const sw = (sweep * Math.PI) / 180;
    const count = Math.max(6, Math.round(Math.abs(sweep) / 22));
    for (let i = 0; i < count; i++) {
      const a = a0 + (sw * (i + 0.5)) / count;
      const rMid = radius / 2;
      boxes.push({
        x: center.x + rMid * Math.cos(a),
        y: center.y + rMid * Math.sin(a),
        angle: a,
        going: radius,
        width: Math.max(20, (2 * Math.PI * rMid * Math.abs(sw)) / (2 * Math.PI) / count),
        z0: (rise * (i + 1)) / count - stepH,
        h: stepH,
      });
    }
    return boxes;
  }
  const total = pathLength(stair.path);
  if (total <= 0) return boxes;
  const count = Math.max(2, Math.round(total / (stair.kind === 'ladder' ? GOING * 1.5 : GOING)));
  for (let i = 0; i < count; i++) {
    const u = (total * (i + 0.5)) / count;
    const { p, dir } = pathAt(stair.path, u);
    boxes.push({
      x: p.x,
      y: p.y,
      angle: Math.atan2(dir.y, dir.x),
      going: total / count,
      width: stair.kind === 'ladder' ? Math.min(stair.width, 8) : stair.width,
      z0: (rise * (i + 1)) / count - stepH,
      h: stepH,
    });
  }
  return boxes;
}

/**
 * Empreinte au sol de l'escalier : anneaux de polygones (pour découper la trémie
 * dans la dalle du niveau supérieur). Retourne [ [pts…], … ].
 */
export function stairFootprint(stair, margin = 2) {
  if (stair.kind === 'spiral') {
    const ring = [];
    const r = stair.radius + margin;
    for (let i = 0; i < 24; i++) {
      const a = (2 * Math.PI * i) / 24;
      ring.push({ x: stair.center.x + r * Math.cos(a), y: stair.center.y + r * Math.sin(a) });
    }
    return [ring];
  }
  const half = stair.width / 2 + margin;
  const rings = [];
  for (let i = 1; i < stair.path.length; i++) {
    const a = stair.path[i - 1];
    const b = stair.path[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len <= 0) continue;
    const dx = (b.x - a.x) / len;
    const dy = (b.y - a.y) / len;
    const nx = -dy * half;
    const ny = dx * half;
    // Quad du segment, prolongé de `half` aux extrémités pour couvrir les coins
    const ax = a.x - dx * margin;
    const ay = a.y - dy * margin;
    const bx = b.x + dx * margin;
    const by = b.y + dy * margin;
    rings.push([
      { x: ax + nx, y: ay + ny },
      { x: bx + nx, y: by + ny },
      { x: bx - nx, y: by - ny },
      { x: ax - nx, y: ay - ny },
    ]);
  }
  return rings;
}

/** Distance d'un point à l'escalier (pour le hit-test) ; retourne null si loin */
export function hitStair(stair, p, tol) {
  if (stair.kind === 'spiral') {
    const d = Math.hypot(p.x - stair.center.x, p.y - stair.center.y);
    return d <= stair.radius + tol ? d : null;
  }
  const half = stair.width / 2;
  let best = null;
  for (let i = 1; i < stair.path.length; i++) {
    const a = stair.path[i - 1];
    const b = stair.path[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
    if (d <= half + tol && (best === null || d < best)) best = d;
  }
  return best;
}

/** Translate un escalier (déplacement à la souris) */
export function translateStair(stair, dx, dy) {
  if (stair.kind === 'spiral') {
    stair.center = { x: stair.center.x + dx, y: stair.center.y + dy };
  } else {
    stair.path = stair.path.map((p) => ({ x: p.x + dx, y: p.y + dy }));
  }
}
